// Club audio: the Icecast mount played through Web Audio with a positional
// panner anchored at the DJ booth. Pure Web Audio + DOM — no Three.js here;
// the render layer feeds us listener poses as plain vectors.
//
// Browsers refuse audio before a user gesture, so the graph is built lazily
// by unlock() (called from a pointer/key handler). Icecast also drops the
// listener connection whenever the source disconnects, so going live again
// means reloading the element with a fresh URL, not just play().

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** Distance falloff: audible everywhere in the club, clearly hot at the booth. */
const REF_DISTANCE = 4;
const ROLLOFF = 1.2;
const RETRY_DELAY_MS = 3000;

export class ClubAudio {
  private element: HTMLAudioElement;
  private context: AudioContext | undefined;
  private panner: PannerNode | undefined;
  private gain: GainNode | undefined;
  private analyser: AnalyserNode | undefined;
  private analyserBuf: Uint8Array<ArrayBuffer> | undefined;

  private unlocked = false;
  private live = false;
  private enabled = true;
  private wantPlaying = false;
  private retryTimer: number | undefined;

  constructor(
    private streamUrl: string,
    private position: Vec3,
  ) {
    this.element = new Audio();
    this.element.crossOrigin = "anonymous";
    this.element.preload = "none";
    // NOT 'stalled': it fires spuriously while a live stream spins up, and
    // reloading src on it aborts the demuxer before playback ever starts.
    this.element.addEventListener("error", () => this.scheduleRetry());
    this.element.addEventListener("ended", () => this.scheduleRetry());
  }

  /** True once the audio graph exists and the context is allowed to run. */
  get audible(): boolean {
    return this.unlocked && this.context?.state === "running" && this.wantPlaying;
  }

  get isLive(): boolean {
    return this.live;
  }

  /**
   * Build/resume the audio graph. MUST be called from a user-gesture handler
   * the first time, or the context stays suspended.
   */
  unlock(): void {
    if (!this.context) {
      this.context = new AudioContext();
      const source = this.context.createMediaElementSource(this.element);

      this.panner = this.context.createPanner();
      this.panner.panningModel = "HRTF";
      this.panner.distanceModel = "inverse";
      this.panner.refDistance = REF_DISTANCE;
      this.panner.rolloffFactor = ROLLOFF;
      this.panner.positionX.value = this.position.x;
      this.panner.positionY.value = this.position.y;
      this.panner.positionZ.value = this.position.z;

      this.gain = this.context.createGain();
      this.analyser = this.context.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyserBuf = new Uint8Array(this.analyser.frequencyBinCount);

      source.connect(this.panner);
      this.panner.connect(this.gain);
      this.gain.connect(this.analyser);
      this.analyser.connect(this.context.destination);
    }
    if (this.context.state === "suspended") void this.context.resume();
    this.unlocked = true;
    this.reconcile();
  }

  /** Called every frame with the authoritative live flag from room state. */
  setLive(live: boolean): void {
    if (live === this.live) return;
    this.live = live;
    this.reconcile();
  }

  /** Point the stream at a different club's mount (street/club transitions). */
  setStream(streamUrl: string, position: Vec3): void {
    // Re-seat the panner even when the URL is unchanged: radio mode may
    // have moved it, and the room needs it back at the booth.
    if (streamUrl === this.streamUrl) {
      this.position = position;
      if (this.panner && !this.radio) {
        this.panner.positionX.value = position.x;
        this.panner.positionY.value = position.y;
        this.panner.positionZ.value = position.z;
      }
      return;
    }
    this.streamUrl = streamUrl;
    this.position = position;
    if (this.panner) {
      this.panner.positionX.value = position.x;
      this.panner.positionY.value = position.y;
      this.panner.positionZ.value = position.z;
    }
    this.stopPlayback();
    this.reconcile();
  }

  /**
   * Radio mode: the stream stops being a thing in the room and becomes a
   * thing in your pocket. The panner stays in the graph (so levels and the
   * VU dressing keep working) but rides along with the listener, which is
   * what "carrying a receiver" actually sounds like.
   */
  setRadio(on: boolean): void {
    if (on === this.radio) return;
    this.radio = on;
    // Leaving radio mode must put the source back where the room expects
    // it. Without this the panner stayed at wherever the listener last
    // stood on the surface, and the club you walked into was silent.
    if (!on && this.panner) {
      this.panner.positionX.value = this.position.x;
      this.panner.positionY.value = this.position.y;
      this.panner.positionZ.value = this.position.z;
    }
  }

  private radio = false;

  /** Move the Web Audio listener to the camera's pose (plain vectors). */
  updateListener(pos: Vec3, forward: Vec3, up: Vec3): void {
    const listener = this.context?.listener;
    if (!listener) return;
    if (this.radio && this.panner) {
      this.panner.positionX.value = pos.x;
      this.panner.positionY.value = pos.y;
      this.panner.positionZ.value = pos.z;
    }
    if (listener.positionX) {
      listener.positionX.value = pos.x;
      listener.positionY.value = pos.y;
      listener.positionZ.value = pos.z;
      listener.forwardX.value = forward.x;
      listener.forwardY.value = forward.y;
      listener.forwardZ.value = forward.z;
      listener.upX.value = up.x;
      listener.upY.value = up.y;
      listener.upZ.value = up.z;
    } else {
      // Safari still lacks the AudioParam form.
      listener.setPosition(pos.x, pos.y, pos.z);
      listener.setOrientation(forward.x, forward.y, forward.z, up.x, up.y, up.z);
    }
  }

  /** Output level 0..1 (post-panner) — drives VU dressing and verification. */
  level(): number {
    if (!this.analyser || !this.analyserBuf) return 0;
    this.analyser.getByteTimeDomainData(this.analyserBuf);
    let sum = 0;
    for (const v of this.analyserBuf) {
      const centered = (v - 128) / 128;
      sum += centered * centered;
    }
    return Math.sqrt(sum / this.analyserBuf.length);
  }

  /** User mute toggle. Returns the new enabled state. */
  toggle(): boolean {
    this.enabled = !this.enabled;
    this.reconcile();
    return this.enabled;
  }

  get isEnabled(): boolean {
    return this.enabled;
  }

  private reconcile(): void {
    if (this.live && this.unlocked && this.enabled) {
      this.startPlayback();
    } else {
      this.stopPlayback();
    }
  }

  private startPlayback(): void {
    if (this.wantPlaying) return;
    this.wantPlaying = true;
    this.loadAndPlay();
  }

  private stopPlayback(): void {
    this.wantPlaying = false;
    if (this.retryTimer !== undefined) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }
    this.element.pause();
    this.element.removeAttribute("src");
    this.element.load();
  }

  private loadAndPlay(): void {
    // Cache-buster: some browsers reuse a dead streaming connection.
    this.element.src = `${this.streamUrl}?t=${Date.now()}`;
    this.element.play().catch(() => this.scheduleRetry());
  }

  private scheduleRetry(): void {
    if (!this.wantPlaying || this.retryTimer !== undefined) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = undefined;
      if (this.wantPlaying) this.loadAndPlay();
    }, RETRY_DELAY_MS);
  }
}
